import React, { useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { useTheme } from 'styled-components';
import { IconButton } from '@mui/material';
import { CloseIcon } from '@edusynch/edusynch-svg-icons';
import { WidgetFiltersLoading } from './WidgetFiltersLoading.component';
import { useDebounce } from 'modules/Core/Hooks';
import EmptyIcon from 'assets/layout/empty-data/oops-icon.svg?react';

import {
  Card,
  Icon,
  Button,
  Input,
  Avatar,
  SelectAutocomplete,
  MUIDatePicker,
} from 'modules/Core/Common';

import * as S from './WidgetFilters.styles';

const MIN_SEARCH_LENGTH = 3;
const I18N_PREFIX = 'modules.widgets.filters';

const DAYS_OPTIONS = [
  {
    id: '7',
    name: i18n.t('common-words.days_count', { count: 7 }) || '7 days',
  },
  {
    id: '30',
    name: i18n.t('common-words.days_count', { count: 30 }) || '30 days',
  },
  {
    id: '12',
    name: i18n.t('common-words.months_count', { count: 12 }) || '12 months',
  },
  {
    id: 'custom',
    name: i18n.t('common-words.custom') || 'Custom',
  },
];

export const WidgetFilters = ({
  widget,
  defaultUserId = null,
  defaultTestId,
  defaultStartDate,
  defaultEndDate,
  defaultDays,
  defaultSearch,
  users = [],
  tests = [],
  onSave,
  onClose,
  isLoading,
  isLoadingTests,
  onSearchChange,
}) => {
  const [selectedUserId, setSelectedUserId] = useState(defaultUserId);
  const [selectedTestId, setSelectedTestId] = useState(defaultTestId);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  // If there are default dates, it means custom was selected previously
  const [days, setDays] = useState(
    defaultStartDate || defaultEndDate ? 'custom' : defaultDays || '7'
  );
  const [tempSearch, setTempSearch] = useState('');
  const [search, setSearch] = useState(defaultSearch || '');
  const theme = useTheme();

  const isCustomPeriod = days === 'custom';
  const hasDateRange = startDate || endDate;

  useDebounce(
    () => {
      if (
        search === tempSearch ||
        (search.length > 0 && search.length < MIN_SEARCH_LENGTH)
      )
        return;

      setTempSearch(search);
      onSearchChange(search);
    },
    [search],
    800
  );

  const handleSelectUser = (userId) => {
    setSelectedUserId(userId);
  };

  const handleRemoveUser = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedUserId(null);
  };

  const handleSave = () => {
    const selectedTest = tests.find((t) => t.id === selectedTestId);
    onSave({
      userId: selectedUserId,
      testId: selectedTestId,
      testName: selectedTest?.name || null,
      startDate: isCustomPeriod ? startDate : null,
      endDate: isCustomPeriod ? endDate : null,
      days: isCustomPeriod ? null : days,
    });
    onClose();
  };

  const handleClearDateRange = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const selectedUser = users.find((user) => user.id === selectedUserId);
  const unselectedUsers = users.filter((user) => user.id !== selectedUserId);

  // Validation: student and period are required
  const hasPeriodOrDateRange = isCustomPeriod ? startDate && endDate : !!days;
  const hasStudentSelected = !!selectedUserId;
  const hasTestSelected = !!selectedTestId;
  const canSave = hasPeriodOrDateRange && hasStudentSelected && hasTestSelected;

  return (
    <Card noPadding style={{ height: '100%' }}>
      <S.Container>
        <S.Header>
          <div>
            <h6>{widget?.name}</h6>
            <p>{i18n.t('common-words.filter')}</p>
          </div>
          <IconButton
            aria-label="close"
            id="close"
            onClick={onClose}
            disabled={isLoading}
            sx={{ marginLeft: 'auto' }}
          >
            <Icon name="close" />
          </IconButton>
        </S.Header>
        <S.Content>
          {/* Days Select */}
          <S.TimeRangeLabel>
            {i18n.t(`${I18N_PREFIX}.time_range`)}:
          </S.TimeRangeLabel>
          <S.DaysSelectRow>
            <S.SelectWrapper>
              <SelectAutocomplete
                id="select-days"
                options={DAYS_OPTIONS}
                placeholder={i18n.t(`${I18N_PREFIX}.select_period`)}
                value={days ? [days] : []}
                onChangeSelect={(value) => setDays(value)}
                borders
                hideSearch
              />
            </S.SelectWrapper>
          </S.DaysSelectRow>

          {/* Date Range - Only visible when Custom is selected */}
          {isCustomPeriod && (
            <S.DateRangeRow>
              <S.DateInputSmall>
                <MUIDatePicker
                  placeholder={i18n.t('common-words.start_date')}
                  inputClick
                  inputTransparent
                  textFieldProps={{
                    placeholder: i18n.t('common-words.start_date'),
                  }}
                  date={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                />
              </S.DateInputSmall>
              <span>{i18n.t('common-words.to')}</span>
              <S.DateInputSmall>
                <MUIDatePicker
                  placeholder={i18n.t('common-words.end_date')}
                  inputClick
                  inputTransparent
                  textFieldProps={{
                    placeholder: i18n.t('common-words.end_date'),
                  }}
                  date={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                />
              </S.DateInputSmall>
              {hasDateRange && (
                <IconButton
                  aria-label="clear-dates"
                  size="small"
                  onClick={handleClearDateRange}
                >
                  <CloseIcon color={theme.config.colors.gray_400} />
                </IconButton>
              )}
            </S.DateRangeRow>
          )}

          {/* Test Select */}
          <S.TestSelectLabel>
            {i18n.t(`${I18N_PREFIX}.test`)}:
          </S.TestSelectLabel>
          <S.SelectWrapper>
            <SelectAutocomplete
              id="select-test"
              options={tests}
              placeholder={
                isLoadingTests
                  ? i18n.t('common-words.loading')
                  : i18n.t(`${I18N_PREFIX}.select_test`)
              }
              searchPlaceholder={i18n.t(`${I18N_PREFIX}.search_test`)}
              value={selectedTestId ? [selectedTestId] : []}
              onChangeSelect={(value) => setSelectedTestId(value)}
              loading={isLoadingTests}
              disabled={isLoadingTests}
              borders
            />
          </S.SelectWrapper>

          {/* User Search Section */}
          <S.UserSearchSection>
            <Input
              id="search-user"
              prependIcon="search"
              medium
              border
              style={{ width: '100%' }}
              dataCy="filter.search-input"
              placeholder={i18n.t(
                `${I18N_PREFIX}.search_user_by_name_or_email`
              )}
              value={search}
              onChange={({ target }) => setSearch(target.value)}
              disabled={isLoading}
            />

            {/* Selected User Tag - Always on top */}

            {/* History Label - Only show if search was made */}
            {selectedUser ? (
              <>
                <S.HistoryLabel>
                  {i18n.t(`${I18N_PREFIX}.selected_user`)}
                </S.HistoryLabel>
                <S.SelectedUsersList>
                  <S.SelectedUserTag>
                    <Avatar
                      src={selectedUser.avatar_url}
                      name={selectedUser.name}
                      size="small"
                    />
                    <span>{selectedUser.name}</span>
                    <button onClick={handleRemoveUser} aria-label="remove user">
                      <CloseIcon />
                    </button>
                  </S.SelectedUserTag>
                </S.SelectedUsersList>
              </>
            ) : null}

            {/* Users List */}
            {isLoading ? (
              <WidgetFiltersLoading />
            ) : tempSearch && unselectedUsers.length > 0 ? (
              <>
                <S.HistoryLabel>
                  {i18n.t(`${I18N_PREFIX}.results`)}
                </S.HistoryLabel>
                <S.UsersList>
                  {unselectedUsers.map((user) => (
                    <S.UserItem
                      key={user.id}
                      onClick={() => handleSelectUser(user.id)}
                    >
                      <Avatar src={user.avatar_url} name={user.name} />
                      <S.UserInfo>
                        <span className="name">{user.name}</span>
                        <span className="email">{user.email}</span>
                      </S.UserInfo>
                    </S.UserItem>
                  ))}
                </S.UsersList>
              </>
            ) : !tempSearch && !selectedUser ? (
              <S.Empty>
                <p>
                  {i18n.t(`${I18N_PREFIX}.min_chars_message`, {
                    count: MIN_SEARCH_LENGTH,
                  })}
                </p>
              </S.Empty>
            ) : (
              tempSearch &&
              !selectedUser && (
                <S.Empty>
                  <S.EmptyMessage>
                    <EmptyIcon />
                    <p>
                      <strong>{i18n.t('commons.filter.empty.ops')}</strong>{' '}
                      <span>{i18n.t('commons.filter.empty.message')}</span>
                    </p>
                  </S.EmptyMessage>
                </S.Empty>
              )
            )}
          </S.UserSearchSection>
        </S.Content>
        <S.Footer>
          <Button medium onClick={handleSave} disabled={isLoading || !canSave}>
            {i18n.t('common-words.apply')}
          </Button>
        </S.Footer>
      </S.Container>
    </Card>
  );
};

WidgetFilters.propTypes = {
  widget: PropTypes.object,
  defaultUserId: PropTypes.any,
  defaultTestId: PropTypes.any,
  defaultStartDate: PropTypes.any,
  defaultEndDate: PropTypes.any,
  defaultDays: PropTypes.string,
  defaultSearch: PropTypes.string,
  users: PropTypes.array,
  tests: PropTypes.array,
  onSave: PropTypes.func,
  onClose: PropTypes.func,
  onSearchChange: PropTypes.func,
  isLoading: PropTypes.bool,
  isLoadingTests: PropTypes.bool,
};
